import { observable, action, reaction, makeObservable } from 'mobx'

import { BlockModel } from '@client/components/Block/model'
import { defaultSettings } from './constants'
import { MIN_WIDTH } from '@client/components/Block/constants'

export default class ImageModel extends BlockModel {
  constructor(data = {}, editor) {
    super(data)

    makeObservable(this)

    this.editor = editor

    this.settings.set({ ...defaultSettings, ...data.settings })

    // Last container width measured in desktop view — used to compute
    // mobileFullWidth correctly when the canvas is in mobile view (where
    // columns may stack and report a wider containerWidth than on desktop).
    this._desktopContainerWidth = 0

    reaction(
      () => this.containerDimensions.width,
      (containerWidth) => {
        const prevDesktopContainerWidth = this._desktopContainerWidth

        if (this.editor?.viewMode !== 'mobile') {
          this._desktopContainerWidth = containerWidth
        }

        const refWidth = this._desktopContainerWidth || containerWidth
        const mobileFullWidth =
          !this.settings.values.fullWidth && +this.settings.values.width >= refWidth

        if (this.editor?.viewMode === 'mobile') {
          this.settings.set({ mobileFullWidth })
          return
        }

        // Only auto-track the container when the image was pinned to exactly the
        // previous desktop column max. Images with an explicitly-set width below
        // that max (even if now wider than a temporarily-smaller container) are
        // left untouched — CSS max-width:100% handles any visual overflow.
        const wasPinnedToMax =
          prevDesktopContainerWidth > 0 &&
          +this.settings.values.width === prevDesktopContainerWidth
        const shouldMaxWidth =
          !this.settings.values.width ||
          wasPinnedToMax ||
          this.settings.values.fullWidth

        if (shouldMaxWidth && containerWidth >= MIN_WIDTH) {
          this.settings.set({
            width: containerWidth.toString(),
            mobileFullWidth,
          })
        } else {
          this.settings.set({ mobileFullWidth })
        }
      }
    )
  }

  @observable containerDimensions = { width: 0, height: 0 }

  @observable isCropping = false

  @observable isCroppingFullWidth = false

  @observable cropImageUrl = ''

  @action setIsCropping = (state) => {
    this.isCropping = state
  }

  @action setIsCroppingFullWidth = (state) => {
    this.isCroppingFullWidth = state
  }

  @action setCropImageUrl = (url) => {
    this.cropImageUrl = url
  }

  @action setContainerDimensions = (obj) => {
    if (!obj || (obj && !obj.width) || (obj && obj.width < MIN_WIDTH)) {
      return
    }
    this.containerDimensions = obj
  }
}

export const NULL = new ImageModel()
